/* eslint-disable @typescript-eslint/no-explicit-any */
import IconEdit from '@assets/icons/edit.svg?react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { MutableRefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePage } from '../../../contexts/usePageContext';
import { PageNameType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import { CustomGridApi } from '../XTable/types';
import { titleStyles } from './infoStyles';

interface InfoArraysProps {
 id: number;
 apiRef: MutableRefObject<CustomGridApi>;
 table: string;
}

const InfoArrays = ({ id, apiRef, table }: InfoArraysProps) => {
 const { t } = useTranslation();
 const apiCurrent = apiRef.current;
 const { pageName } = usePage();
 const th = useTheme();
 const selector = useAppSelector(
  (state: RootState) => state[pageName as PageNameType],
 );
 const { item, structure, success, loading } = selector;

 const tableArraysWithData = useMemo(() => {
  if (!structure) return [];

  return structure
   ?.filter((field) => field.Type === 'array')
   .map((array) => ({
    ...array,
    Data: item?.[array.Name].map((arrayItem: any) => {
     const x = arrayItem;
     const y = Object.entries(x)
      .map(([key, value]) => ({
       key,
       translation: getTranslation(structure, `${array.Name}.${key}`, true),
       value: value === null ? '-' : value,
      }))
      .filter((i: any, key: number) => {
       const currentStructure = findStructure(array.InnerStructure, i.key);
       return currentStructure.Priority;
      })
      .sort((a: any, b: any) => {
       const currentStructureA = findStructure(array.InnerStructure, a.key);
       const currentStructureB = findStructure(array.InnerStructure, b.key);
       return currentStructureA.Priority - currentStructureB.Priority;
      });
     return y;
     // return array.InnerStructure.map((field) => ({
     //  ...field,
     //  Value: arrayItem[field.Name],
     // }));
    }),
   }));
 }, [structure, item]);

 //  console.log(tableArraysWithData);

 const handleEditClick = (tab: number) => {
  apiCurrent.setModalState({
   id,
   action: 'update',
   tab: tab + 1,
  });
 };

 return tableArraysWithData.map((array, index) => (
  <Box sx={{ mb: 3 }} key={array.Name}>
   <Typography sx={titleStyles}>
    {array.Translation}
    <IconButton onClick={() => handleEditClick(index)}>
     <IconEdit style={{ color: th.palette.kemek.darkestGray }} />
    </IconButton>
   </Typography>

   {loading.getItem && (
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.loading')}...
     </Typography>
    </Box>
   )}

   {!loading.getItem && item?.[array.Name]?.length === 0 && (
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.noItemsFound')}
     </Typography>
    </Box>
   )}

   {!loading.getItem && item?.[array.Name]?.length > 0 && (
    <Box
     sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
     }}
    >
     {array.Data.map((arrItem: any, key: number) => (
      <Box
       // eslint-disable-next-line react/no-array-index-key
       key={`${array.Name}-${key}`}
       sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        borderRadius: '4px',
        border: `1px solid ${th.palette.kemek.lightestGray}`,
        p: 2,
       }}
      >
       {arrItem.map((i: any) => (
        <Box
         key={i.key}
         sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
         }}
        >
         <Typography sx={{ fontWeight: 'medium', fontSize: 12 }}>
          {i.translation}
         </Typography>
         <Typography sx={{ fontSize: 12, textAlign: 'right' }}>
          {i.value}
         </Typography>
        </Box>
       ))}
      </Box>
     ))}
    </Box>
   )}
  </Box>
 ));
};

export default InfoArrays;
